<script setup>
/* eslint-disable no-unused-vars */
import { ref, computed, onMounted } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import ThemeToggle from 'dashboard/components/ThemeToggle.vue';
import contentLogo from 'dashboard/assets/images/contentlogo.png';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud, currentAccount } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

// Mostra o nome da CONTA ATUAL no topo (ex.: "Kaiser") em vez do nome fixo
// do produto — pra o operador saber em qual conta está ao trocar de conta.
const sidebarTitle = computed(() => currentAccount.value?.name || 'Pós-Op');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const hasCustomTools = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CAPTAIN_CUSTOM_TOOLS
    ) ||
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.CAPTAIN_V2)
  );
});

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const menuItems = computed(() => {
  return [
    // ── GERAL ──
    {
      name: 'Clinical Dashboard',
      label: 'Dashboard',
      icon: 'i-lucide-activity',
      to: accountScopedRoute('clinical_dashboard'),
      activeOn: ['clinical_dashboard'],
      section: 'geral',
    },
    {
      name: 'Contacts',
      label: 'Pacientes',
      icon: 'i-lucide-heart-pulse',
      to: accountScopedRoute(
        'contacts_dashboard_index',
        {},
        { page: 1, search: undefined }
      ),
      activeOn: [
        'contacts_dashboard_index',
        'contacts_edit',
        'contacts_dashboard_active',
        'contacts_dashboard_segments_index',
        'contacts_edit_segment',
        'contacts_dashboard_labels_index',
        'contacts_edit_label',
      ],
      section: 'geral',
      children: [
        {
          name: 'all_contacts',
          label: 'Todos os pacientes',
          to: accountScopedRoute('contacts_dashboard_index', {}, { page: 1 }),
          activeOn: [
            'contacts_dashboard_index',
            'contacts_edit',
            'contacts_dashboard_segments_index',
            'contacts_edit_segment',
            'contacts_dashboard_labels_index',
            'contacts_edit_label',
          ],
        },
        {
          name: 'active_contacts',
          label: 'Ativos',
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
      ],
    },
    // ── NAVEGAÇÃO ──
    {
      name: 'Conversation',
      label: 'Central de Conversas',
      icon: 'i-lucide-message-circle',
      to: accountScopedRoute('home'),
      activeOn: [
        'home',
        'inbox_conversation',
        'conversation_mentions',
        'conversation_through_mentions',
        'conversation_unattended',
        'conversation_through_unattended',
        'conversations_through_folders',
        'conversations_through_team',
        'conversation_through_inbox',
        'conversations_through_label',
        'folder_conversations',
        'team_conversations',
        'inbox_dashboard',
        'label_conversations',
      ],
      section: 'navegacao',
      children: [
        {
          name: 'all_conversations',
          label: 'Todas',
          to: accountScopedRoute('home'),
          activeOn: ['home', 'inbox_conversation'],
        },
        {
          name: 'mentions',
          label: 'Menções',
          to: accountScopedRoute('conversation_mentions'),
          activeOn: ['conversation_mentions', 'conversation_through_mentions'],
        },
        {
          name: 'unattended',
          label: 'Não atendidas',
          to: accountScopedRoute('conversation_unattended'),
          activeOn: [
            'conversation_unattended',
            'conversation_through_unattended',
          ],
        },
      ],
    },
    {
      name: 'CareAlerts',
      label: 'Alertas',
      icon: 'i-lucide-bell-ring',
      to: accountScopedRoute('care_alerts_index'),
      activeOn: ['care_alerts_index'],
      section: 'navegacao',
    },
    {
      name: 'Reports',
      label: 'Relatórios',
      icon: 'i-lucide-bar-chart-3',
      to: accountScopedRoute('account_overview_reports'),
      activeOn: [
        'account_overview_reports',
        'conversation_reports',
        'agent_reports_index',
        'agent_reports_show',
        'label_reports_index',
        'inbox_reports_index',
        'inbox_reports_show',
        'team_reports_index',
        'team_reports_show',
        'csat_reports',
        'sla_reports',
        'dh_reports',
      ],
      section: 'navegacao',
    },
    {
      name: 'Settings',
      label: 'Gestão',
      icon: 'i-lucide-settings',
      to: accountScopedRoute('general_settings_index'),
      activeOn: [
        'general_settings_index',
        'agent_list',
        'settings_teams_list',
        'settings_teams_new',
        'settings_teams_finish',
        'settings_teams_add_agents',
        'settings_teams_show',
        'settings_teams_edit',
        'settings_teams_edit_members',
        'settings_teams_edit_finish',
        'settings_procedures_list',
        'settings_physicians',
        'settings_inbox_list',
        'settings_inbox_show',
        'settings_inbox_new',
        'settings_inbox_finish',
        'settings_inboxes_page_channel',
        'settings_inboxes_add_agents',
        'labels_list',
        'canned_list',
        'settings_digital_humans_list',
        'settings_digital_humans_edit',
        'settings_digital_humans_templates',
        'settings_llm_keys',
        'settings_knowledge_bases',
      ],
      section: 'navegacao',
    },
  ];
});
</script>

<!-- eslint-disable vue/no-static-inline-styles, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-n-background flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <img
            :src="contentLogo"
            alt="DocIA"
            style="height: 32px; width: auto"
          />
          <span class="sidebar-logo-text">{{ sidebarTitle }}</span>
        </template>
      </div>
      <div v-if="!isEffectivelyCollapsed && isSuperAdmin" class="px-2">
        <SidebarAccountSwitcher
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <ThemeToggle :is-collapsed="isEffectivelyCollapsed" />
        <ComposeConversation align-position="right" @close="onComposeClose">
          <template #trigger="{ toggle, isOpen }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !w-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
              @click="onComposeOpen(toggle)"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-0.5 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <template v-if="!isEffectivelyCollapsed">
          <li class="sidebar-section-header px-2">Geral</li>
        </template>
        <SidebarGroup
          v-for="item in menuItems.filter(i => i.section === 'geral')"
          :key="item.name"
          :name="item.name"
          :label="item.label"
          :icon="item.icon"
          :to="item.to"
          :active-on="item.activeOn"
          :children="item.children"
        />
        <template v-if="!isEffectivelyCollapsed">
          <li class="sidebar-section-header px-2 mt-4">Navegação</li>
        </template>
        <SidebarGroup
          v-for="item in menuItems.filter(i => i.section === 'navegacao')"
          :key="item.name"
          :name="item.name"
          :label="item.label"
          :icon="item.icon"
          :to="item.to"
          :active-on="item.activeOn"
          :children="item.children"
        />
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="sidebar-profile-footer px-2 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
      <div v-if="!isEffectivelyCollapsed" class="sidebar-version-footer">
        <span>DOCIA PLATFORM</span>
        <span>1.29.2</span>
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>
</template>
